import { useEffect, useMemo, useState } from "react";
import { Flag, Mail, MailOpen, MoreVertical, ReplyAll, Star, Trash2 } from "lucide-react";
import { MobileHeader } from "@/mobile/components/ui/MobileHeader";
import { MobileButton } from "@/mobile/components/ui/MobileButton";
import { MobileScreen } from "@/mobile/components/ui/MobileScreen";
import { MobileReaderActionBar } from "@/mobile/components/message/MobileReaderActionBar";
import { MobileReaderMeta } from "@/mobile/components/message/MobileReaderMeta";
import { MobileMessageActionSheet } from "@/mobile/components/message/MobileMessageActionSheet";
import { AttachmentPreview } from "@/features/message/AttachmentPreview";
import { MessageBodyView } from "@/features/message/MessageBodyView";
import { MessageBodyPreloader } from "@/features/message/MessageBodyPreloader";
import { MessageAttachments } from "@/features/message/MessageAttachments";
import type { MailContextMenuItem } from "@/features/shared/MailContextMenu";
import { OutboxMessageReader } from "@/features/outbox/OutboxMessageReader";
import { ScheduledSendReader } from "@/features/outbox/ScheduledSendReader";
import { isFetchedBodyComplete } from "@/lib/mail/messageBodyCacheStatus";
import type { MailScheduledSendEntry } from "@/lib/api/mailScheduledSend";
import type { OutboxQueueItem } from "@/lib/outbox/outboxQueue";
import type { MailAttachment, MailMessageDetail } from "@/lib/types/mail";

type Props = {
  message: MailMessageDetail | null;
  loading: boolean;
  error: string | null;
  accountId?: number | null;
  accountEmail?: string;
  folder?: string;
  seen?: boolean;
  flagged?: boolean;
  favorite?: boolean;
  outboxItem?: OutboxQueueItem | null;
  scheduledSend?: MailScheduledSendEntry | null;
  scheduledBusy?: boolean;
  onBack: () => void;
  onAction?: (actionId: string) => void;
  onRetry?: () => void;
  onOutboxRetry?: () => void;
  onOutboxEdit?: () => void;
  onOutboxDismiss?: () => void;
  onScheduledSendNow?: () => void;
  onScheduledCancel?: () => void;
  bulkSelectionActive?: boolean;
};

export function MobileReaderScreen({
  message,
  loading,
  error,
  accountId,
  accountEmail = "",
  folder = "INBOX",
  seen = true,
  flagged = false,
  favorite = false,
  outboxItem = null,
  scheduledSend = null,
  scheduledBusy = false,
  onBack,
  onAction,
  onRetry,
  onOutboxRetry,
  onOutboxEdit,
  onOutboxDismiss,
  onScheduledSendNow,
  onScheduledCancel,
  bulkSelectionActive = false,
}: Props) {
  const [previewAttachment, setPreviewAttachment] = useState<MailAttachment | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const subject =
    scheduledSend?.subject?.trim() ||
    outboxItem?.subject?.trim() ||
    message?.subject ||
    "(Sans objet)";

  useEffect(() => {
    setPreviewAttachment(null);
    setOverflowOpen(false);
  }, [message?.uid]);

  const overflowItems: MailContextMenuItem[] = useMemo(
    () => [
      {
        id: "replyAll",
        label: "Répondre à tous",
        icon: ReplyAll,
        disabled: bulkSelectionActive,
      },
      {
        id: "toggleImportant",
        label: flagged ? "Retirer important" : "Marquer important",
        icon: Flag,
      },
      {
        id: "toggleFavorite",
        label: favorite ? "Retirer des favoris" : "Ajouter aux favoris",
        icon: Star,
      },
    ],
    [bulkSelectionActive, favorite, flagged]
  );

  function handleOverflowAction(actionId: string) {
    setOverflowOpen(false);
    onAction?.(actionId);
  }

  function handlePreviewToggle(file: MailAttachment | null) {
    setPreviewAttachment(file);
  }

  if (scheduledSend && accountEmail) {
    return (
      <MobileScreen>
        <MobileHeader
          title={subject}
          subtitle="Envoi programmé"
          left={
            <MobileButton variant="icon" onClick={onBack} aria-label="Retour">
              ←
            </MobileButton>
          }
        />
        <ScheduledSendReader
          entry={scheduledSend}
          accountEmail={accountEmail}
          busy={scheduledBusy}
          onSendNow={() => onScheduledSendNow?.()}
          onCancel={() => onScheduledCancel?.()}
        />
      </MobileScreen>
    );
  }

  if (outboxItem && accountEmail) {
    return (
      <MobileScreen>
        <MobileHeader
          title={subject}
          subtitle="File d'envoi"
          left={
            <MobileButton variant="icon" onClick={onBack} aria-label="Retour">
              ←
            </MobileButton>
          }
        />
        <OutboxMessageReader
          item={outboxItem}
          accountEmail={accountEmail}
          onRetry={() => onOutboxRetry?.()}
          onEdit={() => onOutboxEdit?.()}
          onDismiss={() => onOutboxDismiss?.()}
        />
      </MobileScreen>
    );
  }

  const hasBody = message != null && isFetchedBodyComplete(message);
  const bodyPending = message != null && !hasBody && !error && !loading;

  return (
    <MobileScreen>
      <MobileHeader
        title={subject}
        left={
          <MobileButton variant="icon" onClick={onBack} aria-label="Retour">
            ←
          </MobileButton>
        }
        right={
          message ? (
            <>
              <MobileButton
                variant="icon"
                aria-label={seen ? "Marquer comme non lu" : "Marquer comme lu"}
                onClick={() => onAction?.(seen ? "markUnread" : "markRead")}
              >
                {seen ? (
                  <Mail className="h-5 w-5" aria-hidden />
                ) : (
                  <MailOpen className="h-5 w-5" aria-hidden />
                )}
              </MobileButton>
              <MobileButton
                variant="icon"
                aria-label="Supprimer"
                onClick={() => onAction?.("delete")}
              >
                <Trash2 className="h-5 w-5 text-[var(--mail-danger-text)]" aria-hidden />
              </MobileButton>
              <MobileButton
                variant="icon"
                aria-label="Plus d’actions"
                aria-haspopup="dialog"
                aria-expanded={overflowOpen}
                onClick={() => setOverflowOpen(true)}
              >
                <MoreVertical className="h-5 w-5" aria-hidden />
              </MobileButton>
            </>
          ) : null
        }
      />

      {error && !message && !loading ? (
        <div className="mx-4 mt-3 space-y-3 text-center">
          <p className="mail-alert-error px-3 py-2 text-sm" role="alert">
            {error}
          </p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-lg bg-[var(--mail-accent)] px-4 py-2 text-sm font-medium text-white"
            >
              Actualiser le message
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {loading && !message ? (
          <MessageBodyPreloader overlay label="Ouverture du message…" />
        ) : message ? (
          <>
            <MobileReaderMeta
              from={message.from}
              to={message.to}
              cc={message.cc}
              date={message.date}
              accountEmail={accountEmail}
            />
            {message.attachments && message.attachments.length > 0 && accountId != null ? (
              <MessageAttachments
                accountId={accountId}
                folder={folder}
                messageUid={message.uid}
                attachments={message.attachments}
                previewPart={previewAttachment?.part ?? null}
                onPreview={handlePreviewToggle}
              />
            ) : null}
            <div className="relative min-h-0 flex-1 overflow-hidden">
              {loading || bodyPending ? (
                !hasBody ? (
                  <MessageBodyPreloader overlay quiet label="Ouverture…" />
                ) : null
              ) : null}
              {hasBody ? (
                <MessageBodyView
                  html={message.bodyHtml}
                  text={message.bodyText}
                  from={message.from}
                  messageKey={
                    accountId != null
                      ? { accountId, folder, uid: message.uid }
                      : undefined
                  }
                />
              ) : loading || bodyPending ? null : error ? (
                <div className="flex h-full items-center justify-center p-4">
                  <div className="max-w-sm space-y-3 text-center">
                    <p className="mail-alert-error text-sm" role="alert">
                      {error}
                    </p>
                    {onRetry ? (
                      <button
                        type="button"
                        onClick={onRetry}
                        className="rounded-lg bg-[var(--mail-accent)] px-4 py-2 text-sm font-medium text-white"
                      >
                        Actualiser le message
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : message.hasAttachments ? (
                <div className="flex h-full items-center justify-center p-6 text-center text-sm text-[var(--mail-muted)]">
                  Ce message ne contient pas de texte — consultez les pièces jointes.
                </div>
              ) : (
                <div className="flex h-full items-center justify-center p-6 text-center text-sm text-[var(--mail-muted)]">
                  Contenu indisponible. Réessayez ou actualisez le message.
                </div>
              )}
            </div>
            <MobileReaderActionBar
              disabled={bulkSelectionActive || !onAction}
              onReply={() => onAction?.("reply")}
              onForward={() => onAction?.("forward")}
            />
          </>
        ) : null}
      </div>

      <MobileMessageActionSheet
        open={overflowOpen}
        subject={subject}
        items={overflowItems}
        onAction={handleOverflowAction}
        onClose={() => setOverflowOpen(false)}
      />

      {previewAttachment && accountId != null && message ? (
        <div
          data-mail-no-swipe-back
          className="pointer-events-auto fixed inset-0 z-[310] flex flex-col bg-[var(--mail-surface)]"
          style={{
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
          role="dialog"
          aria-modal="true"
          aria-label={`Aperçu ${previewAttachment.filename}`}
        >
          <AttachmentPreview
            variant="full"
            accountId={accountId}
            folder={folder}
            messageUid={message.uid}
            attachment={previewAttachment}
            onClose={() => setPreviewAttachment(null)}
          />
        </div>
      ) : null}
    </MobileScreen>
  );
}
